import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Switch } from '../components/ui/switch';
import {
  User,
  Mail,
  Lock,
  Upload,
  Trash2,
  Loader2,
  Check,
  Eye,
  EyeOff,
  Camera,
  Shield,
  Download,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../config';
import TypedConfirmDialog from '../components/superadmin/TypedConfirmDialog';

const ProfilePage = () => {
  const { user, refreshUser } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [saving2fa, setSaving2fa] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [cancellingDeletion, setCancellingDeletion] = useState(false);
  
  const fileInputRef = useRef(null);

  const pendingDeletion = Boolean(user?.deletion_requested_at && user?.scheduled_purge_at);

  const handleDownloadData = async () => {
    setExporting(true);
    try {
      const { data } = await axios.get(`${API_URL}/users/me/export`);
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json;charset=utf-8',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `doctree-account-export-${(user?.email || 'me').replace(/[^a-z0-9@._-]/gi, '_')}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Your data export downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleRequestDeletion = async () => {
    setDeleting(true);
    try {
      const { data } = await axios.post(`${API_URL}/users/me/deletion-request`, {
        confirmation: user?.email,
      });
      await refreshUser();
      setDeleteOpen(false);
      const when = data.scheduled_purge_at
        ? new Date(data.scheduled_purge_at).toLocaleDateString()
        : 'the end of the grace period';
      toast.success(`Account deletion scheduled for ${when}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not schedule deletion');
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDeletion = async () => {
    setCancellingDeletion(true);
    try {
      await axios.delete(`${API_URL}/users/me/deletion-request`);
      await refreshUser();
      toast.success('Account deletion cancelled');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not cancel deletion');
    } finally {
      setCancellingDeletion(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }

    setSavingProfile(true);
    try {
      await axios.put(`${API_URL}/auth/profile`, { name, email });
      await refreshUser();
      toast.success('Profile updated successfully');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update profile';
      toast.error(message);
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!currentPassword) {
      toast.error('Current password is required');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    setSavingPassword(true);
    try {
      await axios.post(`${API_URL}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Password changed successfully');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to change password';
      toast.error(message);
    } finally {
      setSavingPassword(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size must be less than 2MB');
      return;
    }

    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(`${API_URL}/auth/avatar`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await refreshUser();
      toast.success('Avatar updated');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to upload avatar';
      toast.error(message);
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleRemoveAvatar = async () => {
    try {
      await axios.delete(`${API_URL}/auth/avatar`);
      await refreshUser();
      toast.success('Avatar removed');
    } catch (error) {
      toast.error('Failed to remove avatar');
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
  };

  const effective2faEnabled =
    user?.two_fa_enforced !== null && user?.two_fa_enforced !== undefined
      ? !!user.two_fa_enforced
      : !!user?.two_fa_enabled;

  const handleToggle2fa = async (nextChecked) => {
    if (user?.two_fa_enforced !== null && user?.two_fa_enforced !== undefined) {
      toast.error('2FA is enforced by an administrator');
      return;
    }
    setSaving2fa(true);
    try {
      await axios.put(`${API_URL}/auth/2fa`, { enabled: !!nextChecked });
      await refreshUser();
      toast.success(`Two-factor authentication ${nextChecked ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update 2FA');
    } finally {
      setSaving2fa(false);
    }
  };

  return (
    <div className="p-6 md:p-8 lg:p-12 animate-fade-in max-w-4xl mx-auto" data-testid="profile-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold font-[Manrope] text-slate-900 dark:text-white mb-2">
          Profile Settings
        </h1>
        <p className="text-slate-600 dark:text-slate-400">
          Manage your account settings and preferences
        </p>
      </div>

      <div className="space-y-6">
        {/* Avatar Section */}
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardHeader>
            <CardTitle className="font-[Manrope] text-slate-900 dark:text-white flex items-center gap-2">
              <Camera className="w-5 h-5" />
              Profile Picture
            </CardTitle>
            <CardDescription>
              Upload a profile picture to personalize your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              <div className="relative group">
                <Avatar className="w-24 h-24 border-4 border-slate-200 dark:border-slate-700">
                  <AvatarImage src={user?.avatar_url} />
                  <AvatarFallback className="text-2xl bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                {uploadingAvatar && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
                    <Loader2 className="w-6 h-6 animate-spin text-white" />
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.gif,.webp"
                  className="hidden"
                  onChange={handleAvatarUpload}
                  data-testid="avatar-file-input"
                />
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingAvatar}
                  data-testid="upload-avatar-button"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {user?.avatar_url ? 'Change Photo' : 'Upload Photo'}
                </Button>
                {user?.avatar_url && (
                  <Button
                    variant="ghost"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                    onClick={handleRemoveAvatar}
                    data-testid="remove-avatar-button"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Remove
                  </Button>
                )}
                <p className="text-xs text-slate-500">
                  PNG, JPG, GIF or WebP. Max 2MB.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Profile Information */}
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardHeader>
            <CardTitle className="font-[Manrope] text-slate-900 dark:text-white flex items-center gap-2">
              <User className="w-5 h-5" />
              Profile Information
            </CardTitle>
            <CardDescription>
              Update your name and email address
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpdateProfile} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="John Doe"
                    data-testid="profile-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="john@example.com"
                      className="pl-10"
                      data-testid="profile-email-input"
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end">
                <Button 
                  type="submit" 
                  disabled={savingProfile}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="save-profile-button"
                >
                  {savingProfile ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardHeader>
            <CardTitle className="font-[Manrope] text-slate-900 dark:text-white flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Change Password
            </CardTitle>
            <CardDescription>
              Update your password to keep your account secure
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current-password">Current Password</Label>
                <div className="relative">
                  <Input
                    id="current-password"
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <Separator />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500">Minimum 8 characters</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm New Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>
              
              <div className="flex justify-end">
                <Button 
                  type="submit" 
                  disabled={savingPassword}
                  variant="outline"
                  data-testid="change-password-button"
                >
                  {savingPassword ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Changing...
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Change Password
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Two-Factor Authentication */}
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardHeader>
            <CardTitle className="font-[Manrope] text-slate-900 dark:text-white flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Two-Factor Authentication (Email OTP)
            </CardTitle>
            <CardDescription>
              If enabled, you’ll receive a one-time code by email each time you sign in.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between gap-4">
              <div className="text-sm text-slate-700 dark:text-slate-300">
                Status:{' '}
                <span className="font-medium text-slate-900 dark:text-white">
                  {effective2faEnabled ? 'Enabled' : 'Disabled'}
                </span>
                {user?.two_fa_enforced !== null && user?.two_fa_enforced !== undefined ? (
                  <span className="ml-2 text-xs text-slate-500">(enforced by admin)</span>
                ) : null}
              </div>
              <Switch
                checked={effective2faEnabled}
                onCheckedChange={handleToggle2fa}
                disabled={saving2fa || (user?.two_fa_enforced !== null && user?.two_fa_enforced !== undefined)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Account Info */}
        <Card className="bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-800">
          <CardContent className="py-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600 dark:text-slate-400">Account created</span>
              <span className="text-slate-900 dark:text-white">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                }) : '-'}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Privacy & data */}
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800" data-testid="profile-privacy-card">
          <CardHeader>
            <CardTitle className="font-[Manrope] text-slate-900 dark:text-white flex items-center gap-2">
              <Download className="w-5 h-5" />
              Privacy &amp; data
            </CardTitle>
            <CardDescription>
              Download a copy of your profile, workspaces, and documents you authored. You can also
              schedule permanent account deletion.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              variant="outline"
              onClick={handleDownloadData}
              disabled={exporting}
              data-testid="download-my-data"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Download my data (JSON)
            </Button>

            {pendingDeletion ? (
              <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-4 space-y-3">
                <div className="flex gap-2 text-amber-900 dark:text-amber-100 text-sm">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <div>
                    Account deletion is scheduled for{' '}
                    <strong>
                      {user?.scheduled_purge_at
                        ? new Date(user.scheduled_purge_at).toLocaleString()
                        : 'soon'}
                    </strong>
                    . Until then you can cancel and keep using DocTree. After that date your account
                    and workspaces you solely own are permanently removed.
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={handleCancelDeletion}
                  disabled={cancellingDeletion}
                  data-testid="cancel-account-deletion"
                >
                  {cancellingDeletion ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  Cancel deletion
                </Button>
              </div>
            ) : (
              <div className="rounded-lg border border-red-200 dark:border-red-900/50 p-4 space-y-3">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Deleting your account starts a grace period (configured by the platform, usually 14
                  days). After that, your account and any workspace where you are the only company
                  admin are permanently deleted.
                </p>
                <Button
                  variant="destructive"
                  onClick={() => setDeleteOpen(true)}
                  data-testid="request-account-deletion"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete my account
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <TypedConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete your account?"
        description="Type your email to confirm. Your account will be scheduled for permanent deletion after the platform grace period. You can cancel anytime before then."
        confirmPhrase={user?.email || ''}
        confirmLabel="Schedule deletion"
        loading={deleting}
        onConfirm={handleRequestDeletion}
      />
    </div>
  );
};

export default ProfilePage;
